"use client"

import { useState, useEffect } from "react"
import { FileText, CheckCircle, Lock, Clock } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { getClaimableShares, claimShares, type ClaimableShare } from '@/api/claim'
import { getDeviceId } from '@/utils/device'
import { useToast } from "@/hooks/use-toast"

interface ClaimSharesDialogProps {
  open: boolean
  onClose: () => void
  onClaimed?: () => void
}

export function ClaimSharesDialog({ open, onClose, onClaimed }: ClaimSharesDialogProps) {
  const { toast } = useToast()
  const [shares, setShares] = useState<ClaimableShare[]>([])
  const [loading, setLoading] = useState(false)
  const [claiming, setClaiming] = useState(false)

  useEffect(() => {
    if (open) {
      loadClaimableShares()
    }
  }, [open])

  const loadClaimableShares = async () => {
    setLoading(true)
    try {
      const deviceId = getDeviceId()
      const data = await getClaimableShares(deviceId)
      setShares(data.shares)
    } catch (error) {
      console.error('加载可认领记录失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClaim = async () => {
    setClaiming(true)
    try {
      const deviceId = getDeviceId()
      await claimShares(deviceId) // 认领所有
      
      if (onClaimed) {
        onClaimed()
      }
      toast({
        title: "认领成功",
        description: "历史匿名分享已成功绑定至您的当前账号",
      })
      onClose()
    } catch (error: any) {
      toast({
        title: "认领失败",
        description: error.message || '认领分享失败，请重试',
        variant: "destructive",
      })
    } finally {
      setClaiming(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    const month = date.getMonth() + 1
    const day = date.getDate()
    const hour = date.getHours().toString().padStart(2, '0')
    const minute = date.getMinutes().toString().padStart(2, '0')
    return `${month}/${day} ${hour}:${minute}`
  }


  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left">
            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
            发现未关联的上传记录
          </DialogTitle>
          <DialogDescription className="text-base mt-2 text-left">
            检测到您在登录前上传了{shares.length}个文件，是否要将这些文件添加到您的账号？
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">加载中...</div>
          ) : shares.length === 0 ? (
            <div className="text-center py-8 text-gray-500">没有可认领的记录</div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {shares.map((share) => (
                <div 
                  key={share.id} 
                  className="border border-gray-200 rounded-lg p-4 bg-white hover:border-blue-300 transition-colors w-full"
                >
                  <div className="flex items-start gap-3 w-full min-w-0">
                    {/* 文件图标 */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                    </div>
                    
                    {/* 文件信息 */}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {/* 文件名 - 使用 CSS 处理溢出，超出部分显示省略号 */}
                      <p 
                        className="font-medium text-gray-900 text-sm mb-2 leading-tight truncate"
                        title={share.shareName}
                      >
                        {share.shareName}
                      </p>
                      
                      {/* 文件详情 */}
                      <div className="text-xs text-gray-500 space-y-0.5 mb-2">
                        <div className="flex items-center gap-1">
                          <span>{share.fileCount} 个文件</span>
                          <span className="text-gray-300">•</span>
                          <span>{formatFileSize(share.totalSize)}</span>
                        </div>
                        <div>上传于 {formatDate(share.createdAt)}</div>
                        <div>下载 {share.downloadCount} 次</div>
                      </div>

                      {/* 特性标签 */}
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1.5 text-xs text-blue-600">
                          <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>AES-256 加密</span>
                        </div>
                        {share.expiresAt && new Date(share.expiresAt) > new Date() && (
                          <div className="flex items-center gap-1.5 text-xs text-purple-600">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>自定义过期时间</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={claiming}
            className="flex-1 sm:flex-initial"
          >
            稍后再说
          </Button>
          <Button 
            onClick={handleClaim}
            disabled={claiming || shares.length === 0}
            className="flex-1 sm:flex-initial bg-blue-600 hover:bg-blue-700"
          >
            {claiming ? '添加中...' : `添加到我的账号 (${shares.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

